/* BTS store — shared interactions for the headless-storefront parity sections.
   All init functions are idempotent (guarded by data-bts-i-* attributes) so the
   whole suite can be safely re-run when the theme editor re-renders a section
   (shopify:section:load). In design mode, reveal targets show instantly. */

(function () {
  'use strict';

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode) ||
    document.documentElement.classList.contains('shopify-design-mode');

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ── Scroll reveal ────────────────────────────────────────────── */
  function initReveal() {
    var els = document.querySelectorAll('[data-bts-reveal]:not([data-bts-i-reveal])');
    els.forEach(function (el) { el.setAttribute('data-bts-i-reveal', '1'); });
    if (DESIGN_MODE || !els.length || typeof IntersectionObserver === 'undefined') {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );
    els.forEach(function (el) { obs.observe(el); });
  }

  /* ── Header — mobile panel toggle + hide-on-scroll-down ───────── */
  function initHeaderMenu() {
    var header = document.querySelector('[data-bts-header]');
    var toggle = header && header.querySelector('[data-bts-header-toggle]');
    var panel = document.querySelector('[data-bts-header-panel]');
    if (!header || !toggle || !panel) return;
    if (header.hasAttribute('data-bts-i-header')) return;
    header.setAttribute('data-bts-i-header', '1');

    var closeTimer = null;

    function setOpen(open) {
      header.classList.toggle('is-menu-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (closeTimer) clearTimeout(closeTimer);

      if (open) {
        panel.hidden = false;
        requestAnimationFrame(function () {
          panel.classList.add('is-open');
        });
        document.body.style.overflow = 'hidden';
      } else {
        panel.classList.remove('is-open');
        document.body.style.overflow = '';
        closeTimer = setTimeout(function () {
          if (!header.classList.contains('is-menu-open')) panel.hidden = true;
        }, 300);
      }
    }

    toggle.addEventListener('click', function () {
      setOpen(!header.classList.contains('is-menu-open'));
    });

    panel.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        setOpen(false);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && header.classList.contains('is-menu-open')) setOpen(false);
    });

    window.addEventListener('resize', function () {
      if (window.innerWidth >= 768 && header.classList.contains('is-menu-open')) setOpen(false);
    });

    var lastScrollY = window.scrollY;
    window.addEventListener(
      'scroll',
      function () {
        if (header.classList.contains('is-menu-open')) return;
        var y = window.scrollY;
        if (y < 80) header.classList.remove('is-hidden');
        else if (y > lastScrollY + 5) header.classList.add('is-hidden');
        else if (y < lastScrollY - 5) header.classList.remove('is-hidden');
        lastScrollY = y;
      },
      { passive: true }
    );
  }

  /* ── Cycling hero headline ───────────────────────────────────── */
  function initCyclingHeadline() {
    document.querySelectorAll('[data-bts-cycle]:not([data-bts-i-cycle])').forEach(function (root) {
      root.setAttribute('data-bts-i-cycle', '1');
      var words = root.querySelectorAll('.bts-cycle-word');
      if (words.length < 2) return;
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) return;
      var index = 0;
      setInterval(function () {
        words[index].classList.remove('is-active');
        index = (index + 1) % words.length;
        words[index].classList.add('is-active');
      }, 2800);
    });
  }

  /* ── Generic height-animated accordion (FAQ + product details) ── */
  function setupAccordion(rootSelector, itemSelector, triggerSelector, panelSelector, opts) {
    opts = opts || {};
    document.querySelectorAll(rootSelector).forEach(function (root) {
      var items = root.querySelectorAll(itemSelector);
      items.forEach(function (item) {
        if (item.hasAttribute('data-bts-i-acc')) return;
        item.setAttribute('data-bts-i-acc', '1');
        var trigger = item.querySelector(triggerSelector);
        var panel = item.querySelector(panelSelector);
        if (!trigger || !panel) return;
        var inner = panel.firstElementChild;
        trigger.addEventListener('click', function () {
          var isOpen = item.classList.contains('is-open');
          if (!opts.multi) {
            items.forEach(function (other) {
              if (other === item) return;
              other.classList.remove('is-open');
              var otherPanel = other.querySelector(panelSelector);
              if (otherPanel) otherPanel.style.height = '0px';
              var otherTrigger = other.querySelector(triggerSelector);
              if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
            });
          }
          if (isOpen) {
            item.classList.remove('is-open');
            panel.style.height = '0px';
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('is-open');
            panel.style.height = inner ? inner.offsetHeight + 'px' : 'auto';
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ── AI section: tab toggle + typewriter prompt ──────────────── */
  function initAiSection() {
    document.querySelectorAll('[data-bts-ai]:not([data-bts-i-ai])').forEach(function (root) {
      root.setAttribute('data-bts-i-ai', '1');
      var tabs = root.querySelectorAll('.bts-tab-btn');
      var panels = root.querySelectorAll('.bts-tab-panel');
      var promptEl = root.querySelector('[data-bts-typewriter]');
      var prompts = [];
      var textEl = null;
      var images = [];
      var promptIndex = 0;
      var charIndex = 0;
      var timer = null;
      var started = false;

      if (promptEl) {
        try {
          prompts = JSON.parse(promptEl.getAttribute('data-prompts') || '[]');
        } catch (e) {
          prompts = [];
        }
        textEl = promptEl.querySelector('.bts-prompt-content');
        images = promptEl.querySelectorAll('.bts-prompt-result img');
      }

      function showImage(idx) {
        images.forEach(function (img, i) {
          img.classList.toggle('is-active', i === idx);
          /* Only the active image should load — swap from data-src once. */
          if (i === idx && img.dataset.src && !img.getAttribute('src')) {
            img.setAttribute('src', img.dataset.src);
          }
        });
      }

      function type() {
        if (!textEl || !prompts.length) return;
        var full = prompts[promptIndex] || '';
        if (charIndex <= full.length) {
          textEl.textContent = full.slice(0, charIndex);
          charIndex += 1;
          timer = setTimeout(type, 55);
        } else {
          timer = setTimeout(function () {
            promptIndex = (promptIndex + 1) % prompts.length;
            charIndex = 0;
            showImage(promptIndex);
            type();
          }, 1800);
        }
      }

      function start() {
        if (started || !prompts.length || !textEl) return;
        started = true;
        showImage(0);
        type();
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var target = tab.getAttribute('data-tab');
          tabs.forEach(function (t) { t.classList.toggle('is-active', t === tab); });
          panels.forEach(function (p) {
            p.classList.toggle('is-active', p.getAttribute('data-tab-panel') === target);
          });
          if (target === 'describe') start();
        });
      });

      if (!promptEl || !prompts.length || !textEl) return;

      if (typeof IntersectionObserver === 'undefined') {
        start();
      } else {
        var obs = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                start();
                obs.disconnect();
              }
            });
          },
          { threshold: 0.2 }
        );
        obs.observe(promptEl);
      }
    });
  }

  /* ── Who it's for: prev/next scroller + expand toggle ────────── */
  function initWhoItsFor() {
    document.querySelectorAll('[data-bts-wif]:not([data-bts-i-wif])').forEach(function (root) {
      root.setAttribute('data-bts-i-wif', '1');
      var track = root.querySelector('.bts-wif-track');
      var prevBtn = root.querySelector('[data-wif-prev]');
      var nextBtn = root.querySelector('[data-wif-next]');
      if (track && (prevBtn || nextBtn)) {
        function update() {
          if (prevBtn) prevBtn.disabled = track.scrollLeft <= 8;
          if (nextBtn) nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 8;
        }
        track.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
        if (prevBtn) prevBtn.addEventListener('click', function () {
          track.scrollBy({ left: -track.clientWidth * 0.8, behavior: 'smooth' });
        });
        if (nextBtn) nextBtn.addEventListener('click', function () {
          track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
        });
      }

      root.querySelectorAll('.bts-wif-toggle').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var card = btn.closest('.bts-wif-card');
          if (!card) return;
          var desc = card.querySelector('.bts-wif-desc');
          var open = card.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (desc) {
            var inner = desc.firstElementChild;
            desc.style.height = open && inner ? inner.offsetHeight + 'px' : '0px';
          }
          var openIcon = btn.querySelector('.bts-icon-open');
          var closeIcon = btn.querySelector('.bts-icon-close');
          if (openIcon) openIcon.style.display = open ? 'none' : '';
          if (closeIcon) closeIcon.style.display = open ? '' : 'none';
        });
      });
    });
  }

  /* ── Phone fan scroll spread ──────────────────────────────────── */
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function initPhoneFan() {
    var SPREAD_START = 0.38;
    document.querySelectorAll('[data-bts-phone-fan]:not([data-bts-i-fan])').forEach(function (section) {
      section.setAttribute('data-bts-i-fan', '1');
      var stage = section.querySelector('.bts-phone-stage');
      var slides = Array.prototype.slice.call(section.querySelectorAll('.bts-phone-slide'));
      if (!stage || !slides.length) return;
      var centerIndex = Math.floor(slides.length / 2);
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function layout() {
        var width = stage.clientWidth;
        var isMobile = width < 640;
        var vh = window.innerHeight;
        var edgePadding = isMobile ? 6 : 10;
        var aspect = 530 / 1080;
        var phoneHeight = isMobile ? Math.min(vh * 0.34, 240) : Math.min(vh * 0.46, 460);

        function scaleFor(i) { return 1 - Math.abs(i - centerIndex) * 0.07; }
        function widthFor(i, h) { return h * aspect * scaleFor(i); }
        function minGap(h) {
          var min = 0;
          for (var i = 0; i < slides.length - 1; i++) {
            var w1 = widthFor(i, h), w2 = widthFor(i + 1, h);
            min = Math.max(min, (w1 + w2) / 2);
          }
          return min;
        }
        function maxGap(h) {
          var w0 = widthFor(0, h), wN = widthFor(slides.length - 1, h);
          var span = Math.abs(0 - centerIndex) + Math.abs(slides.length - 1 - centerIndex);
          return (width - edgePadding * 2 - w0 / 2 - wN / 2) / span;
        }

        var gapUnit = 0;
        while (phoneHeight > 140) {
          var mn = minGap(phoneHeight) * 1.05;
          var mx = maxGap(phoneHeight);
          if (mx >= mn) { gapUnit = mx; break; }
          phoneHeight -= 6;
        }
        if (!gapUnit) gapUnit = minGap(phoneHeight) * 1.05;

        stage.style.height = phoneHeight + 'px';
        slides.forEach(function (slide, i) {
          var h = phoneHeight * scaleFor(i);
          var w = h * aspect;
          slide.style.height = h + 'px';
          slide.style.width = w + 'px';
          slide.style.zIndex = String(slides.length - Math.abs(i - centerIndex));
          slide.setAttribute('data-x-target', String((i - centerIndex) * gapUnit));
        });
        applySpread(currentSpread);
      }

      var currentSpread = reduceMotion ? 1 : 0;

      function applySpread(p) {
        currentSpread = p;
        slides.forEach(function (slide) {
          var xTarget = parseFloat(slide.getAttribute('data-x-target') || '0');
          var scale = slide.style.getPropertyValue('--bts-scale') || 1;
          slide.style.transform =
            'translate(-50%, -50%) translateX(' + xTarget * p + 'px) scale(' + scale + ')';
        });
      }

      slides.forEach(function (slide, i) {
        var scale = 1 - Math.abs(i - centerIndex) * 0.07;
        slide.style.setProperty('--bts-scale', String(scale));
      });

      function onScroll() {
        if (reduceMotion) return;
        var rect = section.getBoundingClientRect();
        var vh = window.innerHeight;
        var start = vh; // 'start end'
        var end = vh / 2 - rect.height / 2 + rect.height; // approximate 'center center'
        var total = start - (vh / 2 - rect.height / 2);
        var traveled = start - rect.top;
        var progress = total > 0 ? traveled / total : 0;
        progress = Math.max(0, Math.min(1, progress));
        var spread = progress <= SPREAD_START ? 0 : easeOutCubic(Math.min(1, (progress - SPREAD_START) / (1 - SPREAD_START)));
        applySpread(spread);
      }

      layout();
      window.addEventListener('resize', layout);
      if (!reduceMotion) {
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    });
  }

  /* ── Bento: timeline cell cycles through years ───────────────── */
  function initBentoStory() {
    document.querySelectorAll('[data-bts-bento-story]:not([data-bts-i-story])').forEach(function (root) {
      root.setAttribute('data-bts-i-story', '1');
      var years = root.querySelectorAll('[data-story-year]');
      var dots = root.querySelectorAll('[data-story-dot]');
      var fill = root.querySelector('[data-story-fill]');
      if (!years.length) return;
      var active = 0;
      setInterval(function () {
        active = (active + 1) % years.length;
        years.forEach(function (y, i) { y.classList.toggle('is-active', i === active); });
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i <= active); });
        if (fill) fill.style.height = (active / (years.length - 1)) * 100 + '%';
      }, 1300);
    });
  }

  /* ── Bento: tributes fan out once in view ─────────────────────── */
  function initBentoTribute() {
    document.querySelectorAll('[data-bts-bento-tribute]:not([data-bts-i-trib])').forEach(function (root) {
      root.setAttribute('data-bts-i-trib', '1');
      if (typeof IntersectionObserver === 'undefined') {
        root.classList.add('is-open-tributes');
        return;
      }
      var obs = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              root.classList.add('is-open-tributes');
              obs.disconnect();
            }
          });
        },
        { threshold: 0.6 }
      );
      obs.observe(root);
    });
  }

  /* ── Bento: invite-a-helper loop ──────────────────────────────── */
  function initBentoShare() {
    var EMAIL = 'sister@email.com';
    document.querySelectorAll('[data-bts-bento-share]:not([data-bts-i-share])').forEach(function (root) {
      root.setAttribute('data-bts-i-share', '1');
      var emailEl = root.querySelector('[data-share-email]');
      var btnEl = root.querySelector('[data-share-btn]');
      var newHelper = root.querySelector('[data-share-newhelper]');
      var countEl = root.querySelector('[data-share-count]');
      if (!emailEl || !btnEl) return;

      function run() {
        var i = 0;
        (function typeStep() {
          if (i <= EMAIL.length) {
            emailEl.textContent = EMAIL.slice(0, i);
            i += 1;
            setTimeout(typeStep, 70);
          } else {
            setTimeout(function () {
              btnEl.textContent = 'Sending';
              setTimeout(function () {
                btnEl.textContent = 'Sent';
                if (newHelper) {
                  newHelper.style.opacity = '1';
                  newHelper.style.transform = 'scale(1)';
                  newHelper.style.borderStyle = 'solid';
                }
                if (countEl) countEl.textContent = '4';
                setTimeout(function () {
                  btnEl.textContent = 'Invite';
                  if (newHelper) {
                    newHelper.style.opacity = '0';
                    newHelper.style.transform = 'scale(0.4)';
                    newHelper.style.borderStyle = 'dashed';
                  }
                  if (countEl) countEl.textContent = '3';
                  run();
                }, 2600);
              }, 650);
            }, 450);
          }
        })();
      }
      run();
    });
  }

  /* ── Bento: biography typewriter ──────────────────────────────── */
  function initBentoBio() {
    document.querySelectorAll('[data-bio-text]:not([data-bts-i-bio])').forEach(function (el) {
      el.setAttribute('data-bts-i-bio', '1');
      var full = el.getAttribute('data-bio-full') || '';
      var i = 0;
      (function step() {
        if (i <= full.length) {
          el.textContent = full.slice(0, i);
          i += 1;
          setTimeout(step, 32);
        } else {
          setTimeout(function () { i = 0; step(); }, 3200);
        }
      })();
    });
  }

  /* ── Product buybox: gallery, quantity, add to cart total ────── */
  function initBuybox() {
    document.querySelectorAll('[data-bts-buybox]:not([data-bts-i-buy])').forEach(function (root) {
      root.setAttribute('data-bts-i-buy', '1');
      var mainImg = root.querySelector('.bts-gallery-main img');
      var dots = root.querySelectorAll('.bts-gallery-dots span');
      var thumbs = root.querySelectorAll('.bts-thumb');
      var prevBtn = root.querySelector('.bts-gallery-arrow.prev');
      var nextBtn = root.querySelector('.bts-gallery-arrow.next');
      var images = [];
      thumbs.forEach(function (t) { images.push(t.getAttribute('data-full')); });

      var active = 0;
      function show(i) {
        if (!images.length) return;
        active = (i + images.length) % images.length;
        if (mainImg && images[active]) {
          /* Shopify image_tag adds srcset; updating only src leaves the old image visible. */
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.setAttribute('src', images[active]);
        }
        dots.forEach(function (d, idx) { d.classList.toggle('is-active', idx === active); });
        thumbs.forEach(function (t, idx) { t.classList.toggle('is-active', idx === active); });
      }
      if (prevBtn) prevBtn.addEventListener('click', function () { show(active - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { show(active + 1); });
      thumbs.forEach(function (t, idx) { t.addEventListener('click', function () { show(idx); }); });

      var qtyInput = root.querySelector('[data-bts-qty-input]');
      var minusBtn = root.querySelector('[data-bts-qty-minus]');
      var plusBtn = root.querySelector('[data-bts-qty-plus]');
      /* Sticky mobile ATC bar lives outside `root`, so update every total on the page. */
      var totalEls = document.querySelectorAll('[data-bts-total]');
      var warrantyCheckbox = root.querySelector('[data-bts-warranty-checkbox]');
      var basePrice = parseFloat(root.getAttribute('data-base-price') || '0');
      var warrantyPrice = parseFloat(root.getAttribute('data-warranty-price') || '0');
      var warrantyVariantId = root.getAttribute('data-warranty-variant-id');
      var warrantyIdInput = root.querySelector('[data-bts-warranty-id-input]');

      function updateTotal() {
        var qty = parseInt((qtyInput && qtyInput.value) || '1', 10) || 1;
        var addWarranty = !!(warrantyCheckbox && warrantyCheckbox.checked);
        var total = (basePrice + (addWarranty ? warrantyPrice : 0)) * qty;
        totalEls.forEach(function (el) { el.textContent = '$' + total.toFixed(2); });
        if (warrantyIdInput) warrantyIdInput.disabled = !addWarranty;
      }
      if (minusBtn && qtyInput) {
        minusBtn.addEventListener('click', function () {
          qtyInput.value = String(Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1));
          updateTotal();
        });
      }
      if (plusBtn && qtyInput) {
        plusBtn.addEventListener('click', function () {
          qtyInput.value = String((parseInt(qtyInput.value, 10) || 1) + 1);
          updateTotal();
        });
      }
      if (warrantyCheckbox) warrantyCheckbox.addEventListener('change', updateTotal);
      if (qtyInput) qtyInput.addEventListener('change', updateTotal);
      updateTotal();

      var warrantyToggle = root.querySelector('[data-bts-warranty-details-toggle]');
      var warrantyPanel = root.querySelector('[data-bts-warranty-details-panel]');
      if (warrantyToggle && warrantyPanel) {
        warrantyToggle.addEventListener('click', function () {
          var open = warrantyToggle.getAttribute('aria-expanded') === 'true';
          warrantyToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
          warrantyToggle.closest('.bts-warranty-box').classList.toggle('is-open', !open);
          var inner = warrantyPanel.firstElementChild;
          warrantyPanel.style.height = !open && inner ? inner.offsetHeight + 'px' : '0px';
        });
      }

      var buyForm = root.querySelector('[data-bts-buy-form]');
      var stickyAtc = document.querySelector('[data-bts-sticky-atc]');
      if (buyForm && stickyAtc && typeof IntersectionObserver !== 'undefined') {
        var obs = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '-100px 0px 0px 0px' }
        );
        obs.observe(buyForm);
      }
    });
  }

  function initAll() {
    initHeaderMenu();
    initReveal();
    initCyclingHeadline();
    setupAccordion('[data-bts-faq]', '.bts-faq-item', '.bts-faq-trigger', '.bts-faq-panel');
    setupAccordion('[data-bts-details]', '.bts-detail-row', '.bts-detail-trigger', '.bts-detail-panel');
    initAiSection();
    initWhoItsFor();
    initPhoneFan();
    initBentoStory();
    initBentoTribute();
    initBentoShare();
    initBentoBio();
    initBuybox();
  }

  ready(initAll);

  /* Theme editor: re-run inits after a section is re-rendered or added.
     Safe because every init is guarded by a data-bts-i-* marker. */
  if (DESIGN_MODE) {
    document.addEventListener('shopify:section:load', function () { initAll(); });
    document.addEventListener('shopify:block:select', function () { initAll(); });
  }
})();
